import React, { useState, useEffect, useRef } from 'react';
import { Search, Upload, Filter, BarChart3, Download, Eye, Grid, List, RefreshCw, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDrawer } from '@/pages/luxottica';
import BatchImageUploader from './hashedBulkUpload';

const ImageGallery = ({ imageKind = 'original', directory = 'imported' }) => {
  // State management
  const [images, setImages] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showStats, setShowStats] = useState(false);
  const { openDrawer } = useDrawer();
  
  // Cache for storing page results
  const cacheRef = useRef(new Map());
  const statisticsCacheRef = useRef(null);
  
  // Helper function to generate cache key
  const getCacheKey = (filters) => {
    return `${filters.directory}-${filters.image_kind}-${filters.page}-${filters.size}`;
  };

  const handleFileUpload = (imageKind, directory) => {
    openDrawer(
      <BatchImageUploader
        imageKind={imageKind}
        directory={directory}
      />
    );
  };

  // Filter and pagination state - now using props for imageKind and directory
  const [filters, setFilters] = useState({
    directory: directory,
    image_kind: imageKind,
    page: 1,
    size: 20
  });
  
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  // Configuration
  const API_BASE = 'http://localhost:12132/api'; // Update with your API base URL

  // Update filters when props change and clear cache
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      directory: directory,
      image_kind: imageKind,
      page: 1 // Reset to first page when props change
    }));
    // Clear cache when props change since we're looking at different data
    cacheRef.current.clear();
    statisticsCacheRef.current = null;
  }, [imageKind, directory]);

  // Fetch images based on current filters
  const fetchImages = async (forceRefresh = false) => {
    const cacheKey = getCacheKey(filters);
    
    // Check cache first (unless force refresh)
    if (!forceRefresh && cacheRef.current.has(cacheKey)) {
      const cachedData = cacheRef.current.get(cacheKey);
      setImages(cachedData.images);
      setPagination(cachedData.pagination);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        directory: filters.directory,
        image_kind: filters.image_kind,
        page: filters.page.toString(),
        size: filters.size.toString(),
        include_urls: 'true'
      });
      
      const response = await fetch(`${API_BASE}/images/display?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const images = data.data || [];
      const pagination = data.pagination || {};
      
      setImages(images);
      setPagination(pagination);
      
      // Cache the result
      cacheRef.current.set(cacheKey, {
        images,
        pagination,
        timestamp: Date.now()
      });
      
    } catch (err) {
      setError(`Failed to fetch images: ${err.message}`);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async (forceRefresh = false) => {
    // Check cache first (unless force refresh)
    if (!forceRefresh && statisticsCacheRef.current) {
      setStatistics(statisticsCacheRef.current);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/images/stats`);
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
        // Cache statistics
        statisticsCacheRef.current = data;
      }
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  // Load data on component mount and filter changes
  useEffect(() => {
    fetchImages();
  }, [filters]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  // Handle filter changes (now only for pagination and page size)
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 unless changing page
    }));
    
    // If changing page size, clear cache since page content will change
    if (key === 'size') {
      cacheRef.current.clear();
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    handleFilterChange('page', newPage);
  };

  // Handle explicit refresh
  const handleRefresh = () => {
    // Clear all caches
    cacheRef.current.clear();
    statisticsCacheRef.current = null;
    // Force refresh current data
    fetchImages(true);
    fetchStatistics(true);
  };

  // Clear cache when component unmounts or major changes occur
  useEffect(() => {
    return () => {
      // Optional: Clear cache on unmount to free memory
      // cacheRef.current.clear();
    };
  }, []);

  // Format file size
  const formatFileSize = (sizeKb) => {
    if (!sizeKb) return 'Unknown';
    if (sizeKb < 1024) return `${sizeKb} KB`;
    return `${(sizeKb / 1024).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {/* Current filters display (read-only) */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-700">Directory:</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md">{directory}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Kind:</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-md">{imageKind}</span>
              </div>
            </div>

            {/* Page Size */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Per page:</label>
              <select 
                value={filters.size} 
                onChange={(e) => handleFilterChange('size', parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <button
              onClick={() => handleFileUpload(imageKind, directory)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Upload Images
            </button>
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className={`p-2 rounded ${showStats ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <BarChart3 className="h-4 w-4" />
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 rounded text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title="Refresh and clear cache"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Cache Status Indicator (Optional - for debugging) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-gray-500 mb-2">
              Cache size: {cacheRef.current.size} pages | 
              Current page cached: {cacheRef.current.has(getCacheKey(filters)) ? 'Yes' : 'No'}
            </div>
          )}

          {/* Statistics Panel */}
          {showStats && statistics && (
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-3 rounded">
                  <div className="text-2xl font-bold text-blue-600">{statistics.total_images}</div>
                  <div className="text-sm text-blue-800">Total Images</div>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <div className="text-2xl font-bold text-green-600">{statistics.storage_stats?.total_estimated_size_mb || 0}MB</div>
                  <div className="text-sm text-green-800">Estimated Size</div>
                </div>
                <div className="bg-purple-50 p-3 rounded">
                  <div className="text-2xl font-bold text-purple-600">{Object.keys(statistics.by_directory || {}).length}</div>
                  <div className="text-sm text-purple-800">Directories</div>
                </div>
                <div className="bg-orange-50 p-3 rounded">
                  <div className="text-2xl font-bold text-orange-600">{Object.keys(statistics.by_kind || {}).length}</div>
                  <div className="text-sm text-orange-800">Image Types</div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination Info */}
          <div className="flex justify-between items-center pt-4 border-t mt-4">
            <div className="text-sm text-gray-500">
              Showing {((filters.page - 1) * filters.size) + 1} to {Math.min(filters.page * filters.size, pagination.total_count)} of {pagination.total_count} images
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={!pagination.has_previous}
                className="p-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-700">
                Page {filters.page} of {pagination.total_pages}
              </span>
              <button
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={!pagination.has_next}
                className="p-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Loading images...</span>
          </div>
        )}

        {/* Images Display */}
        {!loading && images.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-2">No images found</div>
            <div className="text-sm text-gray-500">No {imageKind} images found in {directory} directory.</div>
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && !loading && images.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {images.map((image) => (
              <div key={image.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                {/* Image */}
                <div className="aspect-auto bg-gray-100 relative">
                  {image.download_url ? (
                    <img
                      src={image.download_url}
                      alt={image.display_name}
                      className="w-full h-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-gray-200 flex items-center justify-center" style={{display: 'none'}}>
                    <AlertCircle className="h-8 w-8 text-gray-400" />
                  </div>
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
                    <div className="flex gap-2">
                      {image.download_url && (
                        <>
                          <a
                            href={image.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition-colors"
                          >
                            <Eye className="h-4 w-4" />
                          </a>
                          <a
                            href={image.download_url}
                            download={image.display_name}
                            className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition-colors"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Image Info */}
                <div className="p-3">
                  <div className="font-medium text-gray-900 text-sm truncate mb-1">{image.display_name}</div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>{image.width} × {image.height}</div>
                    <div>{formatFileSize(image.estimated_size_kb)}</div>
                    <div>{formatDate(image.created_at)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && !loading && images.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-900">Image</th>
                    <th className="text-left p-4 font-medium text-gray-900">Name</th>
                    <th className="text-left p-4 font-medium text-gray-900">Dimensions</th>
                    <th className="text-left p-4 font-medium text-gray-900">Size</th>
                    <th className="text-left p-4 font-medium text-gray-900">Created</th>
                    <th className="text-left p-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {images.map((image) => (
                    <tr key={image.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div className="w-auto h-12 rounded overflow-hidden">
                          {image.download_url && (
                            <img
                              src={image.download_url}
                              alt={image.display_name}
                              className="w-auto h-full object-contain"
                            />
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-gray-900">{image.display_name}</div>
                        <div className="text-sm text-gray-500">{image.mime_type}</div>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {image.width} × {image.height}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {formatFileSize(image.estimated_size_kb)}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {formatDate(image.created_at)}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {image.download_url && (
                            <>
                              <a
                                href={image.download_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </a>
                              <a
                                href={image.download_url}
                                download={image.display_name}
                                className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;